import { useState } from "react";
import { Icon, IconSymbols } from "../../../../../../components/ui/Icon";
import { CardProps } from "../../../view/components/KanbanBoard/KanbanBoard";
import Pages from "../../../../../../routes/InsightRoutes";
import { useCurrentOrganisationId } from "../../../../../../services/local/organisationContext/OrganisationContextModel";
import { useNavigate, useParams } from "react-router-dom";
import ModalContext from "../../../../../../core/modalZ/context/ModalContext";
import { ActionPerspectiveModalTabs, Enums } from "../../../../../../enums";
import { Tab, Tabs } from "../../../../../../components/ui/Tabs";
import I18n from "../../../../../../core/localization/I18n";
import { RenderXssSafeString } from "../../../../../../core/AntiXss/AntiXssHelper";
import moment from "moment";
import { Timeline } from "../../../../../../components/widgets/TimelineWidget";
import { IF } from "../../../../../../components/hoc/If";
import { ProgressStatus } from "../../../../../../components/ui/ProgressStatus";
import { RagStatus } from "../../../../../../components/ui/RagStatus";
import { Dropdown, DropdownOption } from "@flightpath/coreui/dist/ui/_forms/Dropdown";
import { CommentListView } from "../../../../../change/comments/commentListView/CommentListView_view";
import { PermissionFields } from "../../../../../../contexts/permissions/PermissionsTypes";
import { PerspectiveModalModel } from "./PerspectiveModal_model";
import { useFlightPathUser } from "../../../../../../core/auth_insight";
import React from "react";
import { RevisionHistoryView } from "../../../../../change/revisionHistory/RevisionHistory_view";
import { ActionPerspectiveAssociatedImpactsGrid } from "../../../../../../core/grids/builder/directorBuilder/grids/ActionPerspectiveAssociatedImpactsGrid/ActionPerspectiveAssociatedImpactsGridView_view";
import { Tag } from "../../../../../../components/ui/Tag";
import "./_perspectiveModal.scss";
import { Pill, PillTypes } from "../../../../../../components/ui/Pill";

interface PerspectiveProps {
  card: CardProps;
}

const PerspectiveModal: React.FC<PerspectiveProps> = ({ card: data }) => {
  return (
    <div className="perspective-actions d-flex flex-column p-7">
      <div className="perspective-header">
        <PerspectiveHeaderDetails card={data} />
      </div>
      <div className="perspective-content flex-1 d-flex flex-column">
        <PerspectiveTabDetails card={data} />
      </div>
    </div>
  );
};

const PerspectiveHeaderDetails: React.FC<PerspectiveProps> = ({ card: data }) => {
  const navigate = useNavigate();
  const organisationId = useCurrentOrganisationId();
  const { projectId } = useParams();
  const authUser = useFlightPathUser();
  const [model] = useState(() => new PerspectiveModalModel(organisationId, Number(projectId), authUser, data));

  const progressStatusItems = [
    { key: 1, label: "Not Started" },
    { key: 2, label: "In Progress" },
    { key: 3, label: "Completed" },
    { key: 5, label: "On Hold" },
    { key: 6, label: "Cancelled" }
  ];

  const currentStatus = progressStatusItems.find(item => item.key === data.progressStatus) || progressStatusItems[0];
  const [selectedProgressStatus, setSelectedProgressStatus] = useState(currentStatus.label);

  return (
    <div className="container-fluid p-2 mb-3">
      <div className="row justify-content-between align-items-center">
        <div className="col-auto">
          <h2 className="mb-2">REF No. {data.refNo}</h2>
          <h2 className="m-0">Action Name</h2>
          <h3 className="d-flex align-items-center">
            {data.progressStatus === Enums.ProgressStatus.COMPLETED ? (
              <ProgressStatus state={data.progressStatus} className="mr-2" />
            ) : (
              <RagStatus state={data.ragStatus} className="mr-2" />
            )}
            <span className="mx-2 h3-heading">{data.title}</span>
            <Icon
              className="link__icon"
              symbol={IconSymbols.ArrowUpRight}
              onClick={() => {
                navigate(Pages.projects.actions.extendedView.generateLink(organisationId, projectId, data.id));
                ModalContext.hide();
              }}
            />
          </h3>
        </div>
        <div className="perspective-dropdown col-auto text-right">
          <h2 className="m-0">Status</h2>
          <Dropdown
            items={progressStatusItems}
            value={{ label: selectedProgressStatus }}
            onItemSelected={selectedItem => {
              setSelectedProgressStatus(selectedItem.label);
              model.updateProgressStatus(selectedItem.key);
            }}
            selector="label"
          >
            <DropdownOption label="" />
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

const PerspectiveTabDetails: React.FC<PerspectiveProps> = ({ card: data }) => {
  const organisationId = useCurrentOrganisationId();
  const { projectId } = useParams();
  const [activeTab, setActiveTab] = useState<number>(0);

  const tabIndex = {
    0: ActionPerspectiveModalTabs.Details,
    1: ActionPerspectiveModalTabs.Impacts,
    2: ActionPerspectiveModalTabs.Notes,
    3: ActionPerspectiveModalTabs.History
  };

  const handleTabClick = (tabNumber: number) => setActiveTab(tabNumber);
  return (
    <Tabs
      currTabIdx={activeTab}
      onTabClicked={handleTabClick}
      className="tabs--primary perspectiveModal"
      ulClassName="tabs__menu--primary"
      initialTab={0}
      liClassName="tabs__item--primary perspective-tab w-100"
    >
      <Tab title={tabIndex[0]}>
        <div className="insight-tab-content__space-filler tab-scrollable">
          <PerspectiveDetailView card={data} />
        </div>
      </Tab>

      <Tab title={tabIndex[1]}>
        <div className="insight-tab-content__space-filler mt-3 pt-4" style={{ height: "460px", overflow: "scroll" }}>
          <ActionPerspectiveAssociatedImpactsGrid
            data={data}
            organisationId={organisationId}
            projectId={Number(projectId)}
          />
        </div>
      </Tab>

      <Tab title={`${tabIndex[2]} (${data.noteCount})`}>
        <div className="insight-tab-content__space-filler" style={{ height: "485px", overflow: "scroll" }}>
          <PerspectiveCommentsView card={data} />
        </div>
      </Tab>

      <Tab title={tabIndex[3]}>
        <div className="insight-tab-content__space-filler" style={{ height: "490px", overflow: "scroll" }}>
          <PerspectiveHistoryView card={data} />
        </div>
      </Tab>
    </Tabs>
  );
};

const PerspectiveDetailView: React.FC<PerspectiveProps> = ({ card: data }) => {
  return (
    <div className="mt-5">
      <div className="row mb-3">
        <div className="col">
          <h2 className="mb-1">{I18n.t("forms.description")}</h2>
          {data.description ? (
            <div dangerouslySetInnerHTML={{ __html: RenderXssSafeString(data.description) }}></div>
          ) : (
            <p>{I18n.t("table.noInput")}</p>
          )}
        </div>
      </div>

      <div className="row mb-3">
        <div className="col">
          <h2 className="mb-1">Start Date</h2>
          <p>
            {moment(data.startDate).year() < 2020 && moment(data.endDate).year() < 2020
              ? "-"
              : moment(data.startDate).format("L")}
          </p>
        </div>
        <div className="col">
          <h2 className="mb-1">End Date</h2>
          <p>
            {moment(data.startDate).year() < 2020 && moment(data.endDate).year() < 2020
              ? "-"
              : moment(data.endDate).format("L")}
          </p>
        </div>
      </div>

      <div className="row mb-3 px-2">
        <div className="col-12">
          <h2 className="mb-3">{I18n.t("phrases.timeline")}</h2>
          <Timeline
            startDate={
              data.startDate && new Date(data.startDate).getFullYear() >= 2020 ? new Date(data.startDate) : undefined
            }
            initialEndDate={
              data.endDate && new Date(data.endDate).getFullYear() >= 2020 ? new Date(data.endDate) : undefined
            }
            actualEndDate={
              data.endDate && new Date(data.endDate).getFullYear() >= 2020 ? new Date(data.endDate) : undefined
            }
          />
        </div>
        <div className="col-6 mt-2">
          <IF condition={!!data.startDate && new Date(data.startDate).getFullYear() >= 2020}>
            <p>{moment(data.startDate).format("L")}</p>
          </IF>
          <IF condition={!data.startDate || new Date(data.startDate).getFullYear() < 2020}>
            <p>-</p>
          </IF>
        </div>
        <div className="col-6 mt-2">
          <IF condition={!!data.endDate && new Date(data.endDate).getFullYear() >= 2020}>
            <p className="text-right">{moment(data.endDate).format("L")}</p>
          </IF>
          <IF condition={!data.endDate || new Date(data.endDate).getFullYear() < 2020}>
            <p className="text-right">-</p>
          </IF>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col">
          <h2>Action Type</h2>
          <p>{data.type ? data.type : "N/A"}</p>
        </div>
        <div className="col">
          <h2>Action Owner</h2>
          <p>{data.owner ? data.owner : "N/A"}</p>
        </div>
        <div className="col">
          <h2>Assigned To</h2>
          <p>{data.assignee ? data.assignee : "N/A"}</p>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <h2>Stakeholder Owners</h2>
          {data.projectStakeholderOwners !== "null"
            ? data.projectStakeholderOwners.split("|").map((stakeholder, index) => {
                return (
                  <Pill type={PillTypes.OUTLINE_PRIMARY} key={index} className="m-1">
                    {stakeholder}
                  </Pill>
                );
              })
            : `${I18n.t("phrases.noStakeholderOwner")}`}
        </div>
        <div className="col">
          <h2>{I18n.t("phrases.tags")}</h2>
          {(() => {
            const tags =
              data.labels?.flatMap(e =>
                typeof e === "string"
                  ? e
                      .split("|")
                      .map(part => part.trim())
                      .filter(Boolean)
                  : []
              ) ?? [];

            if (tags.length === 0) {
              return <p className="mb-0">{I18n.t("phrases.noTags")}</p>;
            }

            return tags.map((part, index) => (
              <Tag key={index} tagText={part} isEditable={false} testId={`${index}`} isNew={false} />
            ));
          })()}
        </div>
      </div>
    </div>
  );
};

const PerspectiveCommentsView: React.FC<PerspectiveProps> = ({ card: data }) => {
  const organisationId = useCurrentOrganisationId();
  const { projectId } = useParams();
  const authUser = useFlightPathUser();
  const [model] = useState(() => new PerspectiveModalModel(organisationId, Number(projectId), authUser, data));

  return (
    <div className="col mt-5">
      <CommentListView model={model.commentViewModel} field={PermissionFields.ACTIONS} />
    </div>
  );
};

const PerspectiveHistoryView: React.FC<PerspectiveProps> = ({ card: data }) => {
  const organisationId = useCurrentOrganisationId();
  const { projectId } = useParams();
  const authUser = useFlightPathUser();
  const [model] = useState(() => new PerspectiveModalModel(organisationId, Number(projectId), authUser, data));
  return <RevisionHistoryView model={model.revisionHistoryModel} />;
};

export default PerspectiveModal;
